import { useNavigate, useLocation } from 'react-router-dom';
import CustomerReferenceShell from './CustomerReferenceShell';
import LanguageSelector from './LanguageSelector';
import AppearanceTheme from './AppearanceTheme';
import usePublishedMetadata from '../hooks/usePublishedMetadata';
import { translateMenuLabel } from '../i18n/catalog';
import { useLanguage } from '../contexts/LanguageContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import useAppearance, { appearanceLink } from '../hooks/useAppearance';
import { formatCurrency } from '../lib/utils';

const referenceSections = [['new-order'], ['mass-order'], ['services'], ['orders'],
  ['payments', 'add-funds'], ['faq'], ['tickets'], ['terms'], ['api'], ['subscriptions'],
  ['affiliates'], ['child-panel']];
const slug = entry => String(entry.page_link || '').replace(/^\/+|\/+$/g, '');

// Keep published destinations and authored labels, while using the requested
// reference order and excluding tools that are absent from that navigation.
export function referenceNavigation(appearance, t) {
  const entries = appearance?.menu?.signed || [];
  return referenceSections.flatMap(aliases => {
    let entry = entries.find(value => aliases.includes(slug(value)));
    // Existing saved menus predate the working customer subscriptions route.
    if (!entry && aliases[0] === 'subscriptions' && entries.some(value => slug(value) === 'orders')) {
      entry = { page_link: 'subscriptions', name: 'Subscriptions' };
    }
    if (!entry) return [];
    const path = slug(entry) === 'subscriptions' ? '/subscriptions' : appearanceLink(entry, appearance.pages);
    return path ? [{ path, label: translateMenuLabel(entry, t) }] : [];
  });
}

export const UserLayout = ({ children, title, subtitle, actions, wide = false, metadataManaged = false }) => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const { branding, tenantId, tenant } = useTenant();
  const { data, error: appearanceError, retry: retryAppearance } = useAppearance();
  const currentTenant = tenantId || tenant?.id || tenant?.tenant_id;
  const accountMatchesTenant = !user?.tenant_id || !currentTenant || user.tenant_id === currentTenant;
  const appearance = accountMatchesTenant ? data : null;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageSlugs = { '/dashboard': ['dashboard'], '/settings': ['account', 'settings'], '/order': ['new-order'],
    '/mass-order': ['mass-order'], '/services': ['services'], '/orders': ['orders'], '/payments': ['payments', 'add-funds'],
    '/subscriptions': ['subscriptions'], '/drip-feed': ['drip-feed'], '/tickets': ['tickets'], '/api-docs': ['api'], '/affiliates': ['affiliates'], '/child-panel': ['child-panel'] };
  const metadata = (pageSlugs[pathname] || []).map(value => appearance?.pages?.find(page => page.slug === value)).find(Boolean);
  usePublishedMetadata(metadataManaged ? undefined : metadata, branding?.name);
  const handleLogout = () => { logout(); navigate('/login'); };

  return <AppearanceTheme theme={appearance?.theme} className="customer-reference-theme">
    <CustomerReferenceShell key={`${user?.tenant_id || ''}:${user?.id || ''}`}
      brand={branding?.name || 'SMM Panel'} logoUrl={branding?.logo_url} brandPath="/order"
      navItems={referenceNavigation(appearance, t)} balance={accountMatchesTenant ? formatCurrency(user?.balance || 0) : undefined}
      accountPath="/settings" onLogout={handleLogout} title={title} subtitle={subtitle} actions={actions}
      languageControl={<LanguageSelector />} wide={wide}
      menuLabel={t('nav.toggle')} navigationLabel={t('nav.main')} skipLabel={t('nav.skip')}
      accountLabel={t('copy.Account')} logoutLabel={t('copy.Logout')} balanceLabel={t('common.balance')}>
      {appearanceError && <p role="alert">{appearanceError} <button type="button" onClick={retryAppearance}>{t('copy.Retrymenu')}</button></p>}
      {accountMatchesTenant ? children : <p role="alert">{t('nav.unavailable')}</p>}
    </CustomerReferenceShell>
  </AppearanceTheme>;
};
